// Monetisation → Experiments (contracts/paywall.md §3, §5): A/B tests of the paywall. Admins create a draft with
// 2–4 variants (layout, copy, plans, price-test products, savings badge, close delay, exit offer), then start,
// pause or end it; only one runs at a time and a started experiment's variants are frozen. The results compare
// the variants: assigned and shown players, trials, paid starts, purchases by product, conversion with its 95%
// interval, trial→paid, refunds, estimated revenue and the probability to be best, with conversion over time.

import { type ReactNode, useState } from "react";
import { Link } from "react-router";
import type { Locale } from "../../api/messaging";
import { LOCALE_LABEL, LOCALES } from "../../api/messaging";
import {
  type Experiment,
  type ExperimentDraft,
  type ExperimentResults,
  type ExperimentVariant,
  type VariantResult,
  useCreateExperiment,
  useExperimentResults,
  useExperiments,
  useUpdateExperiment,
} from "../../api/paywall";
import { useConfig, useSetConfig } from "../../api/queries";
import { useAuth, useCan } from "../../auth/AuthContext";
import { tableStyles } from "../../components/table";
import { Button, Card, Chip, ConfirmDialog, Dialog, EmptyState, ErrorState, Field, InlineError, Loading, PageHeader, Segmented, Select, TextInput } from "../../components/ui";
import { formatDateTime, formatShortDate } from "../../lib/dates";
import { fmtInt, fmtPct } from "../../lib/format";
import { EXPERIMENT_NAME_MAX, LAYOUT_LABEL, mergeVariant, PLAN_NAME, readPaywallConfig, STATUS_TONE, VARIANT_IDS, type Variant, type VariantId, variantProblems, WEIGHT } from "../../lib/paywall";
import m from "../messaging/messaging.module.css";
import styles from "../pages.module.css";
import { PaywallPreview, VariantEditor, variantOut, type VariantState, variantState } from "./components";
import p from "./paywall.module.css";

/** One colour per variant, fixed by its id (validated categorical order on the dark surface). */
export const VARIANT_COLOR: Record<VariantId, string> = {
  a: "var(--color-avatar-orbs-1-1)",
  b: "var(--color-avatar-orbs-2-1)",
  c: "var(--color-avatar-orbs-0-1)",
  d: "var(--color-avatar-orbs-3-1)",
};
const AUDIENCE_LABEL = { new: "New players", all_free: "Every free player" } as const;

/** "Classic", "Trial timeline · $4.99 monthly", … */
function describe(v: Variant): string {
  const bits = [LAYOUT_LABEL[v.layout ?? "classic"]];
  for (const [plan, id] of Object.entries(v.products ?? {})) if (id) bits.push(`${PLAN_NAME[plan as keyof typeof PLAN_NAME]}: ${id}`);
  if (v.defaultPlan) bits.push(`${PLAN_NAME[v.defaultPlan]} preselected`);
  if (v.exitOffer) bits.push("exit offer");
  if (v.closeDelaySec) bits.push(`× after ${v.closeDelaySec} s`);
  if (v.showSavings === false) bits.push("no savings badge");
  return bits.join(" · ");
}

type Mode = { kind: "view" } | { kind: "new" } | { kind: "edit"; experiment: Experiment };

export function ExperimentsPage() {
  const canEdit = useCan("paywall.edit");
  const { admin } = useAuth();
  const list = useExperiments();
  const config = useConfig();
  const settings = readPaywallConfig(config.data?.values);
  const [selected, setSelected] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: "view" });
  const items = list.data?.items ?? [];
  const running = items.find((e) => e.status === "running") ?? null;
  const current = items.find((e) => e.id === selected) ?? running ?? items[0] ?? null;

  return (
    <>
      <PageHeader
        title="Experiments"
        meta={
          canEdit ? (
            <span>A/B tests of the paywall. Players are assigned once, by their account, so the results are exact; one experiment runs at a time.</span>
          ) : (
            <span className={styles.readOnly} data-testid="read-only">
              Read-only · experiments need the admin role (you are {admin?.role})
            </span>
          )
        }
        actions={
          canEdit && mode.kind === "view" ? (
            <Button variant="primary" onClick={() => setMode({ kind: "new" })} data-testid="new-experiment">
              New experiment
            </Button>
          ) : null
        }
      />
      {mode.kind !== "view" ? (
        <ExperimentEditor
          experiment={mode.kind === "edit" ? mode.experiment : null}
          allowed={settings.allowedProducts}
          prices={settings.prices}
          defaultVariant={settings.defaultVariant}
          exitOfferReady={settings.exitOffer.enabled}
          onDone={(id) => {
            if (id) setSelected(id);
            setMode({ kind: "view" });
          }}
        />
      ) : list.isPending ? (
        <Card>
          <Loading rows={6} />
        </Card>
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState title="No experiments yet">{canEdit ? "Create one to test a layout, the copy or a price." : "Admins create them here."}</EmptyState>
        </Card>
      ) : (
        <div className={p.layout}>
          <Card padded={false}>
            <div className={tableStyles.wrap} style={{ border: 0 }}>
              <table className={tableStyles.table}>
                <thead>
                  <tr>
                    <th>Experiment</th>
                    <th>Status</th>
                    <th>Audience</th>
                    <th>Variants</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((e) => (
                    <tr key={e.id} data-clickable data-selected={e.id === current?.id || undefined} onClick={() => setSelected(e.id)} data-testid="experiment-row" data-status={e.status}>
                      <td>
                        <button type="button" className={p.name} onClick={() => setSelected(e.id)}>
                          {e.name}
                        </button>
                        <span className={p.when}>
                          {e.startedAt ? `started ${formatDateTime(e.startedAt)}` : `created ${formatDateTime(e.createdAt)}`}
                          {e.endedAt ? ` · ended ${formatDateTime(e.endedAt)}` : ""}
                        </span>
                      </td>
                      <td>
                        <Chip tone={STATUS_TONE[e.status]}>{e.status}</Chip>
                        {e.winner ? <span className={p.when}>winner {e.winner.toUpperCase()}</span> : null}
                      </td>
                      <td>{AUDIENCE_LABEL[e.audience]}</td>
                      <td>
                        <div className={p.variantChips}>
                          {e.variants.map((v) => (
                            <span key={v.id} className={p.when} style={{ margin: 0 }}>
                              <i className={p.swatch} style={{ ["--series" as string]: VARIANT_COLOR[v.id] }} />
                              {v.id.toUpperCase()} {LAYOUT_LABEL[v.config.layout ?? "classic"]} · {fmtPct(v.weight / (e.variants.reduce((a, x) => a + x.weight, 0) || 1))}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          {current ? (
            <ExperimentDetail key={current.id} experiment={current} running={running} canEdit={canEdit} prices={settings.prices} defaultVariant={settings.defaultVariant} onEdit={() => setMode({ kind: "edit", experiment: current })} />
          ) : null}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------- detail

type Action = "start" | "pause" | "end";

function ExperimentDetail({
  experiment: e,
  running,
  canEdit,
  prices,
  defaultVariant,
  onEdit,
}: {
  experiment: Experiment;
  running: Experiment | null;
  canEdit: boolean;
  prices: Record<string, number>;
  defaultVariant: Variant;
  onEdit: () => void;
}) {
  const update = useUpdateExperiment();
  const setConfig = useSetConfig();
  const results = useExperimentResults(e.status === "draft" ? null : e.id);
  const [action, setAction] = useState<Action | null>(null);
  const [winner, setWinner] = useState<string>(e.winner ?? "");
  const [adopt, setAdopt] = useState(false);
  const [locale, setLocale] = useState<Locale>("en");
  const blocker = running && running.id !== e.id ? running : null;

  const total = e.variants.reduce((a, v) => a + v.weight, 0) || 1;
  const share = (w: number) => fmtPct(w / total);
  const run = (status: "running" | "paused" | "ended") =>
    update.mutate(
      // An ended experiment only takes its winner (the API: name and winner once ended).
      { id: e.id, patch: e.status === "ended" ? { winner: (winner || null) as VariantId | null } : { status, ...(status === "ended" ? { winner: (winner || null) as VariantId | null } : {}) } },
      {
        onSuccess: () => {
          const chosen = e.variants.find((v) => v.id === winner);
          if (status === "ended" && adopt && chosen) {
            // The winner's fields over the default paywall's (the API's mergeVariant).
            setConfig.mutate({ key: "paywall.defaultVariant", value: mergeVariant(defaultVariant, chosen.config) as never }, { onSuccess: () => setAction(null) });
          } else setAction(null);
        },
      },
    );

  return (
    <Card
      title={
        <>
          {e.name} <Chip tone={STATUS_TONE[e.status]}>{e.status}</Chip>
        </>
      }
      actions={
        canEdit ? (
          <div className={p.headActions}>
            {e.status === "draft" || e.status === "paused" ? (
              <Button size="sm" variant="secondary" onClick={onEdit} data-testid="edit-experiment">
                Edit
              </Button>
            ) : null}
            {e.status === "draft" || e.status === "paused" ? (
              <Button size="sm" variant="primary" disabled={blocker !== null} title={blocker ? `“${blocker.name}” is running: pause or end it first.` : undefined} onClick={() => setAction("start")} data-testid="start-experiment">
                {e.status === "paused" ? "Resume…" : "Start…"}
              </Button>
            ) : null}
            {e.status === "running" ? (
              <Button size="sm" variant="secondary" busy={update.isPending && action === null} onClick={() => run("paused")} data-testid="pause-experiment">
                Pause
              </Button>
            ) : null}
            {e.status !== "ended" ? (
              <Button size="sm" variant="ghost" onClick={() => setAction("end")} data-testid="end-experiment">
                End…
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setAction("end")} data-testid="pick-winner">
                {e.winner ? "Change winner…" : "Pick the winner…"}
              </Button>
            )}
          </div>
        ) : null
      }
    >
      <div className={p.cardBody} data-testid="experiment-detail">
        {blocker && canEdit && (e.status === "draft" || e.status === "paused") ? (
          <div className={m.notice} data-tone="info" data-testid="one-running">
            <strong>“{blocker.name}” is running.</strong>
            <span>Only one experiment runs at a time: pause or end it before starting this one.</span>
          </div>
        ) : null}
        {action === null ? <InlineError error={update.error} /> : null}
        <p className={styles.faint}>
          {AUDIENCE_LABEL[e.audience]}
          {e.audience === "new" ? " (accounts created after the first start)" : ""} · {e.variants.map((v) => `${v.id.toUpperCase()} ${share(v.weight)} (${fmtInt(e.assigned[v.id] ?? 0)} assigned)`).join(" / ")}
          {e.startedAt ? ` · started ${formatDateTime(e.startedAt)}` : " · not started"}
          {e.endedAt ? ` · ended ${formatDateTime(e.endedAt)}` : ""}
          {e.status === "running" ? " · pause it to change the audience or variants" : ""}
        </p>

        {e.status === "draft" ? null : results.isPending ? (
          <Loading rows={4} />
        ) : results.isError ? (
          <ErrorState error={results.error} onRetry={() => void results.refetch()} />
        ) : (
          <Results data={results.data} />
        )}

        <div className={p.previewLabel}>
          <span className="monoLabel">Variants</span>
          <Select value={locale} onChange={(ev) => setLocale(ev.target.value as Locale)} aria-label="Preview language" style={{ width: 170 }}>
            {LOCALES.map((l) => (
              <option key={l} value={l}>
                {LOCALE_LABEL[l]}
              </option>
            ))}
          </Select>
        </div>
        <div className={p.previews}>
          {e.variants.map((v) => (
            <div key={v.id} data-testid={`variant-preview-${v.id}`}>
              <div className={p.previewLabel}>
                <span>
                  <i className={p.swatch} style={{ ["--series" as string]: VARIANT_COLOR[v.id] }} />
                  <strong>{v.id.toUpperCase()}</strong> · {share(v.weight)}
                </span>
                {e.winner === v.id ? <Chip tone="success">winner</Chip> : null}
              </div>
              <PaywallPreview variant={v.config} locale={locale} prices={prices} fallback={defaultVariant} />
              <span className={p.products}>{describe(v.config)}</span>
            </div>
          ))}
        </div>
      </div>

      <ConfirmDialog
        open={action === "start"}
        title={`${e.status === "paused" ? "Resume" : "Start"} “${e.name}”?`}
        confirmLabel={e.status === "paused" ? "Resume" : "Start"}
        busy={update.isPending}
        error={update.error}
        onCancel={() => setAction(null)}
        onConfirm={() => run("running")}
      >
        <p className={styles.prose}>
          {AUDIENCE_LABEL[e.audience]} are assigned a variant on their next paywall fetch, by their account, and keep it. Pausing it later lets you change weights and configs (never the variant ids).
        </p>
      </ConfirmDialog>
      <Dialog
        open={action === "end"}
        title={e.status === "ended" ? `The winner of “${e.name}”` : `End “${e.name}”?`}
        onClose={() => setAction(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAction(null)} disabled={update.isPending || setConfig.isPending}>
              Cancel
            </Button>
            <Button variant={e.status === "ended" ? "primary" : "danger"} busy={update.isPending || setConfig.isPending} onClick={() => run("ended")} data-testid="end-confirm">
              {e.status === "ended" ? "Save winner" : "End experiment"}
            </Button>
          </>
        }
      >
        {e.status !== "ended" ? <p className={styles.prose}>Assignment stops for good (an ended experiment can't restart) and everyone sees the default paywall. The results stay here.</p> : null}
        <Field label="Winner" hint="Recorded with the experiment.">
          <Select value={winner} onChange={(ev) => setWinner(ev.target.value)} data-testid="end-winner">
            <option value="">No winner</option>
            {e.variants.map((v) => (
              <option key={v.id} value={v.id}>
                {v.id.toUpperCase()} · {describe(v.config)}
              </option>
            ))}
          </Select>
        </Field>
        {winner ? (
          <label className={p.check} style={{ marginTop: 10 }}>
            <input type="checkbox" checked={adopt} onChange={(ev) => setAdopt(ev.target.checked)} data-testid="end-adopt" />
            Also make {winner.toUpperCase()} the default paywall (paywall.defaultVariant)
          </label>
        ) : null}
        <InlineError error={update.error ?? setConfig.error} />
      </Dialog>
    </Card>
  );
}

// ---------------------------------------------------------------- results

/** The results as rows of metrics, one column per variant: easy to compare across, narrow enough for 1024 px. */
const RESULT_ROWS: { key: string; label: string; hint?: string; cell: (v: VariantResult) => ReactNode }[] = [
  { key: "assigned", label: "Assigned", hint: "players, by account", cell: (v) => fmtInt(v.assigned) },
  {
    key: "shown",
    label: "Shown",
    hint: "players · paywalls in all",
    cell: (v) => (
      <>
        {fmtInt(v.impressions.players)}
        <span className={p.products}>{fmtInt(v.impressions.total)} paywalls</span>
      </>
    ),
  },
  { key: "trials", label: "Trial starts", cell: (v) => fmtInt(v.trialStarts) },
  { key: "paid", label: "Paid starts", hint: "paid at once, lifetime included", cell: (v) => fmtInt(v.paidStarts) },
  {
    key: "purchasers",
    label: "Purchasers",
    hint: "purchases by product",
    cell: (v) => {
      const products = Object.entries(v.purchasesByProduct).filter(([, n]) => n > 0);
      return (
        <>
          {fmtInt(v.purchasers)}
          {products.length ? <span className={p.products}>{products.map(([id, n]) => `${id.replace("iqdaily.pro.", "")} ${n}`).join(" · ")}</span> : null}
        </>
      );
    },
  },
  { key: "conversion", label: "Conversion", hint: "purchasers shown it ÷ players shown, 95% interval", cell: () => null },
  {
    key: "trialToPaid",
    label: "Trial → paid",
    hint: "of the trials known so far",
    cell: (v) =>
      v.trialToPaid.rate === null ? (
        <span className={styles.faint} title={`${v.trialToPaid.trials} trials, none ended yet`}>
          —
        </span>
      ) : (
        <>
          {fmtPct(v.trialToPaid.rate)}
          <span className={p.products}>
            {fmtInt(v.trialToPaid.converted)} of {fmtInt(v.trialToPaid.known)} known
          </span>
        </>
      ),
  },
  { key: "refunds", label: "Refunds", cell: (v) => fmtInt(v.refunds) },
  { key: "revenue", label: "Est. revenue", hint: "USD list prices, paid periods", cell: (v) => `$${fmtInt(Math.round(v.estRevenueUsd))}` },
  { key: "pBest", label: "P(best)", hint: "Beta-Binomial, on conversion", cell: () => null },
];

function Results({ data }: { data: ExperimentResults }) {
  const max = Math.max(0.0001, ...data.variants.map((v) => v.conversion.high ?? v.conversion.rate ?? 0));
  const pct = (v: number) => `${Math.min(100, (v / max) * 100)}%`;
  const leader = [...data.variants].sort((a, b) => b.probabilityBest - a.probabilityBest)[0];
  return (
    <>
      <div className={tableStyles.wrap} data-testid="results">
        <table className={tableStyles.table}>
          <thead>
            <tr>
              <th>Per variant</th>
              {data.variants.map((v) => (
                <th key={v.id} data-align="right" data-testid="result-variant" style={{ ["--series" as string]: VARIANT_COLOR[v.id] }}>
                  <i className={p.swatch} />
                  {v.id.toUpperCase()} · {v.weight}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {RESULT_ROWS.map((row) => (
              <tr key={row.key} data-testid={`result-${row.key}`}>
                <th scope="row" className={p.metric}>
                  {row.label}
                  {row.hint ? <span className={p.products}>{row.hint}</span> : null}
                </th>
                {data.variants.map((v) => (
                  <td key={v.id} data-align="right" className={tableStyles.mono} style={{ ["--series" as string]: VARIANT_COLOR[v.id] }}>
                    {row.key === "conversion" ? (
                      <div className={p.ci} data-testid="ci">
                        {v.conversion.low !== null && v.conversion.high !== null && v.conversion.rate !== null ? (
                          <span className={p.ciBar} title={`95% interval ${fmtPct(v.conversion.low, 2)} – ${fmtPct(v.conversion.high, 2)}`}>
                            <span className={p.ciRange} style={{ left: pct(v.conversion.low), width: `calc(${pct(v.conversion.high)} - ${pct(v.conversion.low)})` }} />
                            <span className={p.ciDot} style={{ left: pct(v.conversion.rate) }} />
                          </span>
                        ) : null}
                        <span>
                          {fmtPct(v.conversion.rate, 2)}
                          {v.conversion.low !== null && v.conversion.high !== null ? (
                            <span className={p.products}>
                              {fmtPct(v.conversion.low, 2)} – {fmtPct(v.conversion.high, 2)}
                            </span>
                          ) : null}
                        </span>
                      </div>
                    ) : row.key === "pBest" ? (
                      <div className={p.best}>
                        <span className={p.meter}>
                          <span style={{ width: `${Math.round(v.probabilityBest * 100)}%` }} />
                        </span>
                        <span data-testid="p-best">{fmtPct(v.probabilityBest)}</span>
                      </div>
                    ) : (
                      row.cell(v)
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.unpriced.length ? (
        <div className={m.notice} data-tone="warning" data-testid="unpriced">
          <strong>No list price for {data.unpriced.join(", ")}.</strong>
          <span>
            Those purchases count as $0 in the revenue estimate: add their USD price in <Link to="/monetisation/paywall">Paywall settings</Link>.
          </span>
        </div>
      ) : null}
      <p className={styles.footNote} style={{ marginTop: 0 }}>
        {formatShortDate(data.from)} – {formatShortDate(data.to)} (UTC). Revenue is estimated from paywall.prices (USD list prices). P(best) from {fmtInt(data.draws)} draws.
        {leader && data.variants.some((v) => v.conversion.shown > 0)
          ? leader.probabilityBest >= 0.95
            ? ` ${leader.id.toUpperCase()} is very likely best (${fmtPct(leader.probabilityBest)}).`
            : " No clear winner yet: call it when one variant stays above 95%."
          : ""}
      </p>
    </>
  );
}

// ---------------------------------------------------------------- editor

interface EditVariant {
  id: VariantId;
  weight: string;
  state: VariantState;
}

function ExperimentEditor({
  experiment,
  allowed,
  prices,
  defaultVariant,
  exitOfferReady,
  onDone,
}: {
  experiment: Experiment | null;
  allowed: string[];
  prices: Record<string, number>;
  defaultVariant: Variant;
  exitOfferReady: boolean;
  onDone: (id: string | null) => void;
}) {
  const create = useCreateExperiment();
  const update = useUpdateExperiment();
  const [name, setName] = useState(experiment?.name ?? "");
  const [audience, setAudience] = useState<ExperimentDraft["audience"]>(experiment?.audience ?? "new");
  const [variants, setVariants] = useState<EditVariant[]>(() =>
    (experiment?.variants ?? [
      { id: "a", weight: 50, config: {} },
      { id: "b", weight: 50, config: { layout: "timeline" } },
    ]).map((v) => ({ id: v.id, weight: String(v.weight), state: variantState(v.config) })),
  );
  const [open, setOpen] = useState<VariantId>("a");
  /** A paused experiment keeps its variant ids: weights and configs change, variants aren't added or removed. */
  const paused = experiment?.status === "paused";
  const [locale, setLocale] = useState<Locale>("en");

  const out: ExperimentVariant[] = variants.map((v) => ({ id: v.id, weight: Number(v.weight), config: variantOut(v.state) }));
  const totalWeight = out.reduce((a, v) => a + (Number.isFinite(v.weight) ? v.weight : 0), 0);
  const problems = [
    ...(!name.trim() ? ["Name the experiment."] : []),
    ...(variants.length < 2 || variants.length > 4 ? ["2 to 4 variants."] : []),
    ...out.flatMap((v) => (!Number.isInteger(v.weight) || v.weight < WEIGHT.min || v.weight > WEIGHT.max ? [`${v.id.toUpperCase()}: a weight from ${WEIGHT.min} to ${WEIGHT.max}.`] : [])),
    ...out.flatMap((v) => variantProblems(v.config, allowed).map((x) => `${v.id.toUpperCase()}: ${x}`)),
  ];
  const busy = create.isPending || update.isPending;
  const setVariant = (id: VariantId, patch: Partial<EditVariant>) => setVariants((list) => list.map((v) => (v.id === id ? { ...v, ...patch } : v)));
  const current = variants.find((v) => v.id === open) ?? variants[0]!;

  const save = () => {
    const draft: ExperimentDraft = { name: name.trim(), audience, variants: out };
    if (experiment) update.mutate({ id: experiment.id, patch: draft }, { onSuccess: () => onDone(experiment.id) });
    else create.mutate(draft, { onSuccess: (made) => onDone(made.id) });
  };

  return (
    <Card
      title={experiment ? `Edit “${experiment.name}”` : "New experiment"}
      actions={
        <div className={p.headActions}>
          <Button variant="ghost" onClick={() => onDone(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} disabled={problems.length > 0} title={problems[0]} onClick={save} data-testid="experiment-save">
            {experiment ? (paused ? "Save" : "Save draft") : "Create draft"}
          </Button>
        </div>
      }
    >
      <div className={p.cardBody} data-testid="experiment-editor">
        <InlineError error={create.error ?? update.error} />
        <div className={p.inline}>
          <Field label="Name">
            <TextInput value={name} maxLength={EXPERIMENT_NAME_MAX} onChange={(e) => setName(e.target.value)} placeholder="e.g. Trial timeline vs classic" data-testid="experiment-name" style={{ minWidth: 320 }} />
          </Field>
          <Field label="Audience" hint={audience === "new" ? "Accounts created after the start." : "Every free player, new or not."}>
            <Segmented
              label="Audience"
              value={audience}
              onChange={setAudience}
              options={[
                { value: "new", label: "New players" },
                { value: "all_free", label: "Every free player" },
              ]}
            />
          </Field>
        </div>
        <p className={styles.faint}>
          A variant's empty fields use the default paywall (<Link to="/monetisation/paywall">Paywall settings</Link>). Weights are relative: {variants.map((v) => `${v.id.toUpperCase()} ${totalWeight ? fmtPct(Number(v.weight) / totalWeight) : "—"}`).join(" · ")}.
        </p>
        <div className={p.previewLabel}>
          <Segmented
            label="Variant"
            value={current.id}
            onChange={setOpen}
            options={variants.map((v) => ({ value: v.id, label: `${v.id.toUpperCase()} · ${LAYOUT_LABEL[v.state.config.layout ?? "classic"]}` }))}
          />
          <span className={p.headActions}>
            <Button
              size="sm"
              variant="ghost"
              disabled={variants.length >= 4 || paused}
              title={paused ? "A paused experiment keeps its variants (players stay in theirs)" : undefined}
              onClick={() => {
                const id = VARIANT_IDS.find((x) => !variants.some((v) => v.id === x))!;
                setVariants([...variants, { id, weight: "25", state: variantState({}) }]);
                setOpen(id);
              }}
              data-testid="add-variant"
            >
              + Variant
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={variants.length <= 2 || paused}
              onClick={() => {
                const rest = variants.filter((v) => v.id !== current.id).map((v, i) => ({ ...v, id: VARIANT_IDS[i]! }));
                setVariants(rest);
                setOpen("a");
              }}
            >
              Remove {current.id.toUpperCase()}
            </Button>
          </span>
        </div>
        <div className={p.variantCard} key={current.id} data-testid={`variant-${current.id}`}>
          <div className={p.variantHead}>
            <i className={p.swatch} style={{ ["--series" as string]: VARIANT_COLOR[current.id] }} />
            <strong>{current.id.toUpperCase()}</strong>
            <Field label="Weight">
              <TextInput className={p.weight} type="number" min={WEIGHT.min} max={WEIGHT.max} value={current.weight} onChange={(e) => setVariant(current.id, { weight: e.target.value })} data-testid="variant-weight" />
            </Field>
          </div>
          <div className={p.split}>
            <VariantEditor
              value={current.state}
              onChange={(u) => setVariants((list) => list.map((v) => (v.id === current.id ? { ...v, state: typeof u === "function" ? u(v.state) : u } : v)))}
              allowed={allowed}
              prices={prices}
              locale={locale}
              onLocale={setLocale}
              exitOfferReady={exitOfferReady}
              fallback={defaultVariant}
              testPrefix={`variant-${current.id}`}
            />
            <div>
              <div className={p.previewLabel}>
                <span className="monoLabel">Preview · {locale}</span>
              </div>
              <PaywallPreview variant={variantOut(current.state)} locale={locale} prices={prices} fallback={defaultVariant} />
            </div>
          </div>
        </div>
        {problems.length ? (
          <ul className={p.problems} data-testid="experiment-problems">
            {problems.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </Card>
  );
}
